/**
 * Bucket 6 (views/booking + views/account) temporary shared helpers.
 * Follow-up: merge into domain types once the TS-ZERO sweep lands.
 */

import type { ComponentType, ReactNode } from 'react';

/** Coerce Next.js router query values to a single string. */
export const queryParamAsString = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

/**
 * Build view component props in spec files where only a subset of fields is
 * exercised by the test. Same contract as `makeCmsProps`: caller supplies the
 * target shape via the generic parameter.
 */
export const makeViewProps = <T>(overrides: any): T => overrides as unknown as T;

/** 4th argument to `scrollToElement` (scroll-to-bottom flag). */
export const SCROLL_TO_ELEMENT_DEFAULT = false as const;

/**
 * Legacy widgets typed with DOM `Node` / `Element` instead of ReactNode.
 * Use at call sites inside bucket-6 views until upstream prop types are fixed.
 */
export const asLegacyDomNode = <T>(value: T): Node => value as unknown as Node;

export const asLegacyDomChildren = (children: ReactNode): Node => children as unknown as Node;

/**
 * Components whose return type is broken by DOM generics (e.g. `.map<Node>`).
 * Accepts widgets that incorrectly return DOM Node/Node[].
 */
export const asLegacyJsxComponent = <P>(
  component: ComponentType<P> | ((props: P) => Node | Node[] | null),
): ComponentType<P> => component as unknown as ComponentType<P>;

/** Legacy widgets expecting DOM `HTMLCollection` for React children. */
export const asLegacyHtmlCollection = (children: ReactNode): HTMLCollection =>
  children as unknown as HTMLCollection;

/** Bridge legacy DOM casts back to ReactNode for JSX children slots. */
export const asLegacyReactNode = (value: unknown): ReactNode => value as unknown as ReactNode;

/** Coerce partial product fixtures (specs / utils tests). */
export const asProductFixture = (value: unknown): Product => value as unknown as Product;

/** Coerce partial proposal fixtures (specs / utils tests). */
export const asProposalFixture = (value: unknown): Proposal => value as unknown as Proposal;

/** Coerce partial accommodation fixtures (specs / utils tests). */
export const asAccommodationFixture = (value: unknown): Accommodation =>
  value as unknown as Accommodation;

/** Coerce partial additionalServices fixtures. */
export const asAdditionalServicesFixture = (value: unknown): Proposal['additionalServices'] =>
  value as Proposal['additionalServices'];

/** Coerce Next.js router query param to boolean (truthy string check). */
export const queryParamAsBoolean = (value: string | string[] | undefined): boolean | undefined => {
  const str = queryParamAsString(value);
  if (str === undefined) {
    return undefined;
  }
  return str === 'true' || str === '1';
};

/** Legacy BookingNavbarContext setters typed as no-arg void fns. */
export const asNavbarContextSetter = <T>(setter: T): (() => void) =>
  setter as unknown as () => void;

/** Cast ReduxState for legacy booking/lead selectors. */
export const asBookingSelectorState = (state: ReduxState): AppState => state as unknown as AppState;

/** Fee keys from proposal price composition reduce. */
export type ProposalFeeTaxesMap = Partial<
  Record<'SUBSCRIPTION' | 'PORT_TAXES' | 'TOURISTIC_TAXES', string>
>;

/** Coerce fee/tax map from price composition reduce. */
export const asProposalFeeTaxes = (value: unknown): ProposalFeeTaxesMap =>
  value as ProposalFeeTaxesMap;

/** Static page container with pageId/pageType metadata. */
export type StaticPageContainer<P = Record<string, never>> = ComponentType<P> & {
  pageId?: string;
  pageType?: string;
  disableFooter?: boolean;
  disableHeader?: boolean;
  requireAuthentication?: boolean;
};

/** Widen errorBoundary/connect output for static page metadata assignment. */
export const asStaticPageContainer = <P>(component: ComponentType<P>): StaticPageContainer<P> =>
  component as unknown as StaticPageContainer<P>;

/** Coerce GQL hook `data` to a typed shape. */
export const asGqlData = <T>(data: unknown): T => data as T;

/** Coerce partial attendee-price fixtures (specs). */
export const asAttendeePriceFixture = (value: unknown): AttendeePrice =>
  value as unknown as AttendeePrice;

/** Coerce partial household fixtures (specs). */
export const asHouseholdFixture = (value: unknown): any => value;

/** Coerce partial stay fixtures (specs). */
export const asStayEntityFixture = (value: unknown): StayEntity => value as unknown as StayEntity;

/** Cast partial info-banner args (optional layout fields omitted at call sites). */
export const asInfoBannerArgs = (value: Record<string, unknown>): any => value;

/** Coerce travel-section departure for tracking labels. */
export const asTravelSectionBound = (value: unknown): { label?: string; cityId?: string } =>
  (value ?? {}) as { label?: string; cityId?: string };

/** Cast partial RouterLocation fixtures in router specs. */
export const asRouterLocation = (location: unknown): RouterLocation => location as RouterLocation;

/** Mutable query-params bag for shallow routing updates. */
export const asMutableQueryParams = (
  queryParams: RouterLocation['queryParams'],
): Record<string, string> => queryParams as Record<string, string>;

/** Cast createElement('script') result for legacy GTM snippets. */
export const asHtmlScriptElement = (el: HTMLElement): HTMLScriptElement => el as HTMLScriptElement;

/** Cast session-storage JSON blobs spread into object literals. */
export const asSessionStorageRecord = (value: unknown): Record<string, unknown> =>
  (value ?? {}) as Record<string, unknown>;

/** Cast ABTasty session-storage payload for object spread in tracking. */
export const asAbTastyEventRecord = (value: unknown): Record<string, unknown> =>
  (value ?? {}) as Record<string, unknown>;

/** Cast HTMLInputElement in form container specs. */
export const asHtmlInputElement = (el: HTMLElement): HTMLInputElement => el as HTMLInputElement;
